import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ADMIN_TABLE_BADGE_GROUP_PARENT,
} from "@shared/helpers/opportunity-group-admin-copy";
import {
  flattenSummerCampaignLeadMatches,
  MATCH_REASON_LABELS,
  type SummerCampaignLeadMatchInstance,
  type SummerCampaignLeadMatchItem,
} from "@shared/helpers/summer-campaign-lead-admin";
import { formatOpportunitySchedule } from "@shared/helpers/format-opportunity-schedule";
import { type FC, useEffect, useMemo, useState } from "react";
import { LeadActionTooltip } from "./LeadActionTooltip";
import { LEAD_ACTION_TOOLTIPS } from "./lead-actions-copy";

export type LeadMatchRow = SummerCampaignLeadMatchInstance;

export type LeadMatchSearchCriteria = {
  teamNames: string[];
  categoryNames: string[];
  attendanceLabel: string;
  availabilityLabel?: string;
};

type Props = {
  matches: SummerCampaignLeadMatchItem[];
  selectedIds: Record<string, boolean>;
  onToggle: (id: string, row: LeadMatchRow, checked: boolean) => void;
  onSelectAll: () => void;
  onClear: () => void;
  loading?: boolean;
  searchCriteria?: LeadMatchSearchCriteria;
  onOpenFullTable?: () => void;
  onPreview?: (
    opportunityId: string,
    options?: { isGroupParent?: boolean },
  ) => void;
};

function formatMatchSchedule(match: SummerCampaignLeadMatchInstance) {
  return formatOpportunitySchedule(match);
}

function MatchReasonChips({
  reasons,
}: {
  reasons?: (keyof typeof MATCH_REASON_LABELS)[];
}) {
  if (!reasons?.length) return null;
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap" mt={0.25}>
      {reasons.map((r) => (
        <Chip
          key={r}
          size="small"
          label={MATCH_REASON_LABELS[r]}
          variant="outlined"
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      ))}
    </Stack>
  );
}

function InstanceRow({
  match,
  selectedIds,
  onToggle,
  onPreview,
  nested,
}: {
  match: LeadMatchRow;
  selectedIds: Record<string, boolean>;
  onToggle: (id: string, row: LeadMatchRow, checked: boolean) => void;
  onPreview?: (opportunityId: string, options?: { isGroupParent?: boolean }) => void;
  nested?: boolean;
}) {
  return (
    <FormControlLabel
      sx={{
        m: 0,
        px: nested ? 2 : 1,
        py: 0.5,
        borderRadius: "0.5rem",
        alignItems: "flex-start",
        bgcolor: selectedIds[match.id] ? "rgba(0, 180, 216, 0.06)" : undefined,
        "&:hover": { bgcolor: "action.hover" },
      }}
      control={
        <Checkbox
          size="small"
          checked={!!selectedIds[match.id]}
          disabled={match.alreadyRegistered}
          onChange={(_, checked) => onToggle(match.id, match, checked)}
        />
      }
      label={
        <Stack gap={0.25} pt={0.25} minWidth={0}>
          <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
            <Typography
              variant="body2"
              fontWeight={nested ? 400 : 500}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPreview?.(match.id);
              }}
              sx={{
                cursor: onPreview ? "pointer" : "default",
                textDecoration: onPreview ? "underline" : "none",
                textUnderlineOffset: 2,
              }}
            >
              {match.title ?? match.id}
              {match.alreadyRegistered ? " (כבר רשום)" : ""}
            </Typography>
            {match.offPreferences ? (
              <Chip
                size="small"
                label={nested ? "חלקי" : "התאמה חלקית"}
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            ) : null}
          </Stack>
          {(formatMatchSchedule(match) || match.location) && (
            <Typography variant="caption" color="text.secondary">
              {[formatMatchSchedule(match), match.location].filter(Boolean).join(" · ")}
            </Typography>
          )}
          <MatchReasonChips reasons={match.matchReasons} />
        </Stack>
      }
    />
  );
}

function GroupMatchRow({
  group,
  selectedIds,
  onToggle,
  onPreview,
  expanded,
  onToggleExpand,
}: {
  group: Extract<SummerCampaignLeadMatchItem, { kind: "group" }>;
  selectedIds: Record<string, boolean>;
  onToggle: (id: string, row: LeadMatchRow, checked: boolean) => void;
  onPreview?: (opportunityId: string, options?: { isGroupParent?: boolean }) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const selectable = group.instances.filter((m) => !m.alreadyRegistered);
  const selectedCount = selectable.filter((m) => selectedIds[m.id]).length;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "0.75rem",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={0.5}
        px={1}
        py={0.75}
        onClick={onToggleExpand}
        sx={{
          bgcolor: expanded ? "action.hover" : undefined,
          cursor: "pointer",
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          aria-label="הרחב קבוצת מופעים"
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Stack flex={1} minWidth={0} gap={0.25}>
          <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
            <Typography
              variant="body2"
              fontWeight={600}
              onClick={(e) => {
                e.stopPropagation();
                onPreview?.(group.id, { isGroupParent: true });
              }}
              sx={{
                cursor: onPreview ? "pointer" : "default",
                textDecoration: onPreview ? "underline" : "none",
                textUnderlineOffset: 2,
              }}
            >
              {group.title ?? group.id}
            </Typography>
            <Chip
              size="small"
              label={ADMIN_TABLE_BADGE_GROUP_PARENT}
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
            />
            {group.offPreferences ? (
              <Chip
                size="small"
                label="התאמה חלקית"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div">
            {[
              group.location,
              `${group.instances.length} מופעים`,
              selectedCount > 0 ? `נבחרו ${selectedCount}` : null,
              expanded ? null : "לחצו להצגת המופעים",
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <MatchReasonChips reasons={group.matchReasons} />
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        <Stack gap={0.25} pb={0.5}>
          {group.instances.map((instance) => (
            <InstanceRow
              key={instance.id}
              match={instance}
              selectedIds={selectedIds}
              onToggle={onToggle}
              onPreview={onPreview}
              nested
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function SearchCriteriaSummary({ criteria }: { criteria: LeadMatchSearchCriteria }) {
  const parts = [
    criteria.teamNames.length ? `צוות: ${criteria.teamNames.join(", ")}` : null,
    criteria.categoryNames.length
      ? `קטגוריה: ${criteria.categoryNames.join(", ")}`
      : null,
    `סוג: ${criteria.attendanceLabel}`,
    criteria.availabilityLabel ? `זמינות: ${criteria.availabilityLabel}` : null,
  ].filter(Boolean);

  return (
    <Typography variant="caption" color="text.secondary" component="div">
      חיפשנו משימות פתוחות לפי: {parts.join(" · ")}
    </Typography>
  );
}

export const LeadMatchedOpportunitiesPanel: FC<Props> = ({
  matches,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  loading,
  searchCriteria,
  onOpenFullTable,
  onPreview,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const flatMatches = useMemo(() => flattenSummerCampaignLeadMatches(matches), [matches]);

  useEffect(() => {
    setExpandedGroups({});
  }, [matches]);
  const exactItems = matches.filter((m) => !m.offPreferences);
  const partialItems = matches.filter((m) => m.offPreferences);
  const selectable = flatMatches.filter((m) => !m.alreadyRegistered);
  const selectedCount = selectable.filter((m) => selectedIds[m.id]).length;

  const renderItems = (items: SummerCampaignLeadMatchItem[]) =>
    items.map((item) =>
      item.kind === "group" ? (
        <GroupMatchRow
          key={item.id}
          group={item}
          selectedIds={selectedIds}
          onToggle={onToggle}
          onPreview={onPreview}
          expanded={!!expandedGroups[item.id]}
          onToggleExpand={() =>
            setExpandedGroups((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
          }
        />
      ) : (
        <InstanceRow
          key={item.id}
          match={item}
          selectedIds={selectedIds}
          onToggle={onToggle}
          onPreview={onPreview}
        />
      ),
    );

  if (loading) {
    return <Typography color="text.secondary">טוען משימות מתאימות...</Typography>;
  }

  if (!matches.length) {
    return (
      <Stack gap={1.5} py={2}>
        {searchCriteria ? <SearchCriteriaSummary criteria={searchCriteria} /> : null}
        <Typography color="text.secondary">
          לא נמצאו משימות פתוחות בצוותי הליד. נסה/י חיפוש בטבלת המשימות המלאה.
        </Typography>
        {onOpenFullTable ? (
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.fullTable}>
            <Button variant="contained" size="small" onClick={onOpenFullTable}>
              טבלה מלאה
            </Button>
          </LeadActionTooltip>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      {searchCriteria ? <SearchCriteriaSummary criteria={searchCriteria} /> : null}

      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          משימות מוצעות ({matches.length})
        </Typography>
        <Stack direction="row" gap={1}>
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.selectAllMatches}>
            <Button size="small" onClick={onSelectAll}>
              בחר הכל
            </Button>
          </LeadActionTooltip>
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.clearMatches}>
            <Button size="small" color="inherit" onClick={onClear}>
              נקה
            </Button>
          </LeadActionTooltip>
        </Stack>
      </Stack>

      <Stack gap={1.5} sx={{ maxHeight: 480, overflowY: "auto" }}>
        {exactItems.length > 0 ? (
          <Stack gap={0.75}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              מתאים בדיוק לבחירות הליד ({exactItems.length})
            </Typography>
            <Stack gap={0.75}>{renderItems(exactItems)}</Stack>
          </Stack>
        ) : null}
        {partialItems.length > 0 ? (
          <Stack gap={0.75}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              התאמה חלקית ({partialItems.length})
            </Typography>
            <Stack gap={0.75}>{renderItems(partialItems)}</Stack>
          </Stack>
        ) : null}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        נבחרו {selectedCount} מתוך {selectable.length} מופעים
      </Typography>
    </Stack>
  );
};
